import React from 'react';
import {
  formatApiBodyPreview,
  formatApiHeadersPreview,
  formatApiPathPreview,
  createEditorRow,
  paramDefsFromEditorRows,
  resolvedParamsFromEditorRows,
  structuredOverridesForPreviewMode,
} from '../utils/apiQueryParams';
import './ApiQueryParamsEditor.css';

const DATE_SCRIPT_HINT = (
  <>
    Date values support <code>now</code>, <code>today</code>, <code>N days ago</code> (daily, UTC),
    or concrete <code>YYYY-MM-DD</code>. Numbers and booleans are sent as JSON types, not strings.
  </>
);

export default function ApiQueryParamsEditor({
  rows = [],
  onChange,
  endpoint = '',
  apiSource = null,
  pathPreview = null,
  allowAdd = true,
  keyPlaceholder = 'country',
  valuePlaceholder = 'us',
  hint = null,
  previewMode = 'query',
  showPreview = true,
  previewOptions = null,
}) {
  const rowOverrides = resolvedParamsFromEditorRows(rows);
  const structuredOverrides = structuredOverridesForPreviewMode(previewMode, rowOverrides);
  const formatOptions = previewOptions || undefined;
  const source = apiSource || {
    endpoint,
    query_param_defs: previewMode === 'query' ? paramDefsFromEditorRows(rows) : [],
    body_param_defs: previewMode === 'body' ? paramDefsFromEditorRows(rows) : [],
    header_param_defs: previewMode === 'headers' ? paramDefsFromEditorRows(rows) : [],
  };
  const resolvedEndpoint = endpoint || String(source.endpoint || '').trim();

  const preview = previewMode === 'body'
    ? formatApiBodyPreview(source, structuredOverrides, formatOptions)
    : previewMode === 'headers'
      ? formatApiHeadersPreview(source, structuredOverrides, formatOptions)
      : (pathPreview != null ? pathPreview : formatApiPathPreview(source, structuredOverrides, formatOptions));

  const updateRow = (index, patch) => {
    const next = rows.map((row, i) => (i === index ? { ...row, ...patch } : row));
    onChange?.(next);
  };

  const addRow = () => onChange?.([...(rows || []), createEditorRow()]);

  const removeRow = (index) => {
    const next = rows.filter((_, i) => i !== index);
    onChange?.(next.length ? next : [createEditorRow()]);
  };

  const displayRows = rows?.length ? rows : [createEditorRow()];
  const showPathPreview = showPreview && resolvedEndpoint && previewMode === 'query';
  const showBodyPreview = showPreview && resolvedEndpoint && previewMode === 'body';
  const showHeadersPreview = showPreview && previewMode === 'headers';

  return (
    <div className="api-query-params-editor">
      {hint && <div className="hint api-query-params-hint">{hint}</div>}
      {previewMode === 'body' && (
        <div className="hint api-query-params-hint">{DATE_SCRIPT_HINT}</div>
      )}
      <div className="api-query-params-table-wrap">
        <table className="api-query-params-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Value</th>
              {allowAdd && <th aria-label="Actions" />}
            </tr>
          </thead>
          <tbody>
            {displayRows.map((row, index) => (
              <tr key={row.id || `row-${index}`}>
                <td>
                  <input
                    type="text"
                    className="form-control"
                    value={row.key || ''}
                    placeholder={keyPlaceholder}
                    onChange={(e) => updateRow(index, { key: e.target.value, label: e.target.value })}
                  />
                </td>
                <td>
                  <input
                    type="text"
                    className="form-control"
                    value={row.value || ''}
                    placeholder={valuePlaceholder}
                    onChange={(e) => updateRow(index, { value: e.target.value })}
                  />
                </td>
                {allowAdd && (
                  <td className="api-query-params-actions">
                    <button
                      type="button"
                      className="btn btn-sm btn-secondary"
                      onClick={() => removeRow(index)}
                      disabled={displayRows.length <= 1 && !row.key && !row.value}
                      title="Remove parameter"
                    >
                      <i className="fas fa-times" aria-hidden />
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {allowAdd && (
        <button type="button" className="btn btn-sm btn-secondary api-query-params-add" onClick={addRow}>
          <i className="fas fa-plus" /> Add parameter
        </button>
      )}
      {showPathPreview && (
        <div className="api-query-path-preview">
          <span className="api-query-path-label">Request path</span>
          <code>{preview || '—'}</code>
        </div>
      )}
      {showBodyPreview && (
        <div className="api-query-path-preview api-query-body-preview">
          <span className="api-query-path-label">JSON body (Content-Type: application/json)</span>
          <pre className="api-query-body-json">{preview || '{}'}</pre>
        </div>
      )}
      {showHeadersPreview && (
        <div className="api-query-path-preview api-query-body-preview">
          <span className="api-query-path-label">Request headers</span>
          <pre className="api-query-body-json">{preview || '{}'}</pre>
        </div>
      )}
    </div>
  );
}
